// ============================================================================
// skpConvert - parse a raw SketchUp (.skp) file in the browser and turn it
// into an in-memory self-contained GLB, so the user can drop a .skp straight
// into the meadow without any desktop step.
//
// Parsing runs on a dedicated Web Worker (bundled with the app) so big files
// do not jank the render loop. The worker uses `openskp` (MIT, pure JS, no
// SketchUp needed) for both legacy and modern .skp containers, bakes the full
// scene to world space and exports a GLB with texture images embedded. A
// lightweight magic-number pass fixes the mime labels the library guesses.
// ============================================================================

export interface SkpConvertResult {
  /** freshly created object URL of the produced GLB */
  url: string;
  /** display name to keep (the original .skp filename) */
  name: string;
  glbBytes: number;
  prims: number;
  tris: number;
  textures: number;
}

export class SkpConvertError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SkpConvertError';
  }
}

function sniffImageMime(bytes: Uint8Array): string | null {
  if (bytes.length < 12) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) return 'image/webp';
  return null;
}

export function convertSkpToGlb(file: File): Promise<SkpConvertResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./skpConvert.worker.ts', import.meta.url), { type: 'module' });

    const timer = window.setTimeout(() => {
      worker.terminate();
      reject(new SkpConvertError('解析超时：这个 .skp 太大或格式过新，已中止。可改用 tools/sketchup-exporter 导出 .glb 后导入。'));
    }, 120_000);

    worker.onmessage = async (ev: MessageEvent) => {
      const msg = ev.data as {
        ok: boolean;
        glb?: ArrayBuffer;
        stats?: { glbBytes: number; prims: number; tris: number; textures: number };
        error?: string;
      };
      window.clearTimeout(timer);
      worker.terminate();
      if (!msg.ok || !msg.glb) {
        reject(new SkpConvertError(`无法解析该 .skp（${msg.error ?? '未知错误'}）。请确认是 SketchUp 2013+ 保存的文件；若仍失败可改用桌面插件导出 .glb 再导入。`));
        return;
      }
      try {
        const blob = new Blob([msg.glb], { type: 'model/gltf-binary' });
        const url = URL.createObjectURL(blob);
        const s = msg.stats!;
        if (s.prims === 0) {
          URL.revokeObjectURL(url);
          reject(new SkpConvertError('这个 .skp 里没有任何可渲染的网格。'));
          return;
        }
        resolve({ url, name: file.name, glbBytes: s.glbBytes, prims: s.prims, tris: s.tris, textures: s.textures });
      } catch (err) {
        reject(new SkpConvertError(`生成场景失败：${String((err as Error)?.message ?? err)}`));
      }
    };

    worker.onerror = (ev) => {
      window.clearTimeout(timer);
      worker.terminate();
      reject(new SkpConvertError(`解析器加载失败（${ev.message ?? 'worker error'}）。可改用桌面插件导出 .glb 再导入。`));
    };

    file.arrayBuffer().then((buffer) => {
      worker.postMessage({ buffer }, [buffer]);
    }).catch((err) => {
      window.clearTimeout(timer);
      worker.terminate();
      reject(new SkpConvertError(`读取文件失败：${String(err)}`));
    });
  });
}

export { sniffImageMime };
